import { DateTime } from 'luxon';
import murmur from 'murmurhash-js';
import * as showdown from 'showdown';
import axios from 'smg/axios';
import { HandleRequestError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import Modal from 'smg/modal';
import { G } from 'smg/querystring';
import * as template from 'smg/template';
import { UUID } from 'smg/uuid';

const $modalTemplate = document.querySelector('#modal-product-type-quiz-template') as HTMLTemplateElement;

type productName = 'Lapel Pins' | 'Challenge Coins' | 'Patches' | 'Lanyards' | 'Belt Buckles' | 'Key Chains' | 'Keychains' | 'Medals';

function shortProductPlural(product: productName): string {
    switch (product) {
    case 'Lapel Pins':
        return 'Pins';
    case 'Challenge Coins':
        return 'Coins';
    case 'Patches':
        return 'Patches';
    case 'Lanyards':
        return 'Lanyards';
    case 'Belt Buckles':
        return 'Belt Buckles';
    case 'Key Chains':
    case 'Keychains':
        return 'Keychains';
    case 'Medals':
        return 'Medals';
    default:
        return product;
    }
}

function shortProductSingular(product: productName): string {
    switch (product) {
    case 'Lapel Pins':
        return 'Pin';
    case 'Challenge Coins':
        return 'Coin';
    case 'Patches':
        return 'Patch';
    case 'Lanyards':
        return 'Lanyard';
    case 'Belt Buckles':
        return 'Belt Buckle';
    case 'Key Chains':
    case 'Keychains':
        return 'Keychain';
    case 'Medals':
        return 'Medal';
    default:
        return product;
    }
}

declare interface quizResponse {
    type: string;
    message: string;
}

interface typePage {
    product: string;
    type: string;
    description: string;
    turnaroundTime: string;
    image: string;
    link: string;
}

export declare interface QuizRequest {
    product: productName;
    questions: { question: string; answer: string }[];
    typePages: typePage[];
}

// storeQuizRequest stores the quiz results in local storage,
// setting a query string param to recall that on a different page
function storeQuizRequest(req: QuizRequest): string {
    const id = murmur(UUID());
    window.localStorage.setItem(`quiz-req-${id}`, JSON.stringify(req));
    return id.toString();
}

export function GetQuizRequest(): QuizRequest | null {
    const id = G().Get('quiz');
    if (!id) {
        return null;
    }

    const req = window.localStorage.getItem(`quiz-req-${id}`);
    if (!req) {
        return null;
    }

    return JSON.parse(req) as QuizRequest;
}

function showQuiz($el: HTMLElement) {
    const product = $el.getAttribute('data-product-type-quiz-for') as productName;

    const $body = template.Clone($modalTemplate).firstElementChild as HTMLElement;
    const $currStepNumber = $body.querySelector('#modal-curr-step-number') as HTMLElement;
    const $totalStepNumber = $body.querySelector('#modal-total-step-number') as HTMLElement;
    const $continue = $body.querySelector('#modal-continue') as HTMLButtonElement;
    const $back = $body.querySelector('#modal-back') as HTMLButtonElement;
    const $submit = $body.querySelector('#modal-submit') as HTMLButtonElement;
    const $progress = $body.querySelector('#modal-step-progress') as HTMLElement;

    const $steps = $body.querySelectorAll<HTMLFieldSetElement>(`.modal-quiz-step[data-product="${CSS.escape(product)}"]`);

    let currStep = -1;
    let $currStep: HTMLFieldSetElement;
    const totalSteps = $steps.length;

    const getForm = () => m.Element.querySelector('form') as HTMLFormElement;

    const updateContinueDisabled = () => {
        $continue.disabled = !getForm().checkValidity();
        $submit.disabled = !getForm().checkValidity();
    };

    ['input', 'change'].forEach(event => {
        $body.addEventListener(event, updateContinueDisabled);
    });

    const getDateNeededContainer = () => $currStep.querySelector<HTMLFieldSetElement>('.modal-date-needed-container');
    const getOtherContainer = () => $currStep.querySelector<HTMLFieldSetElement>('.modal-other-container');

    $body.querySelectorAll<HTMLInputElement>('input[type="radio"],input[type="checkbox"]').forEach($el => {
        $el.addEventListener('change', () => {
            const $dateNeededContainer = getDateNeededContainer();
            if ($dateNeededContainer) {
                const show = $el.checked && $el.classList.contains('modal-show-date-needed');

                $dateNeededContainer.classList.toggle('d-none', !show);
                $dateNeededContainer.disabled = !show;
                updateContinueDisabled();
            }

            const $otherContainer = getOtherContainer();
            if ($otherContainer) {
                const show = $el.checked && $el.classList.contains('modal-show-other');

                $otherContainer.classList.toggle('d-none', !show);
                $otherContainer.disabled = !show;
                updateContinueDisabled();
            }
        });
    });

    const nextStep = () => {
        if ($currStep) {
            $currStep.classList.add('d-none');
            $currStep.disabled = true;

            if (!ReportValidity(getForm())) {
                return;
            }
        }

        currStep++;

        if (currStep) {
            $back.classList.remove('d-none');
        } else {
            $back.classList.add('d-none');
        }

        $currStep = $steps[currStep];
        $currStepNumber.textContent = String(currStep + 1);
        $currStep.classList.remove('d-none');
        $currStep.disabled = false;
        $totalStepNumber.textContent = String(totalSteps);

        $progress.style.width = `${((currStep + 1) / totalSteps) * 100}%`;

        if (currStep === totalSteps - 1) {
            $continue.classList.add('d-none');
            $submit.classList.remove('d-none');
        }

        updateContinueDisabled();
    };

    const prevStep = () => {
        if ($currStep) {
            $currStep.classList.add('d-none');
            $currStep.disabled = true;
        }

        currStep--;

        if (currStep) {
            $back.classList.remove('d-none');
        } else {
            $back.classList.add('d-none');
        }

        $currStep = $steps[currStep];
        $currStepNumber.textContent = String(currStep + 1);
        $currStep.classList.remove('d-none');
        $currStep.disabled = false;
        $totalStepNumber.textContent = String(totalSteps);

        $progress.style.width = `${((currStep + 1) / totalSteps) * 100}%`;

        $continue.classList.remove('d-none');
        $submit.classList.add('d-none');

        updateContinueDisabled();
    };

    $continue.addEventListener('click', () => {
        nextStep();
    });

    $back.addEventListener('click', () => {
        prevStep();
    });

    const getStepAnswer = ($step: HTMLFieldSetElement): { question: string; answer: string } => {
        const $question = $step.querySelector('.modal-question') as HTMLElement;

        let answer = '';
        const $textarea = $step.querySelector('textarea') as HTMLTextAreaElement;
        if ($textarea) {
            answer = $textarea.value;
        } else {
            const $checked = $step.querySelectorAll<HTMLInputElement>('input:checked');
            if ($checked.length) {
                answer = Array.from($checked).map($el => $el.value).join(', ');
            }
        }

        const $dateNeeded = $step.querySelector<HTMLInputElement>('.modal-date-needed-container:not(.d-none) input');
        if ($dateNeeded) {
            const daysFromToday = Math.ceil(DateTime.fromISO($dateNeeded.value).diffNow('days').days);
            answer += ` - Date needed ${$dateNeeded.value} (${daysFromToday} days from today)`;
        }

        const $other = $step.querySelector<HTMLInputElement>('.modal-other-container:not(.d-none) input');
        if ($other) {
            answer += ` - ${$other.value}`;
        }

        if (answer === '') {
            answer = 'No answer provided';
        }

        return {
            question: $question.textContent || '',
            answer,
        };
    };

    $submit.addEventListener('click', () => {
        $currStep.classList.add('d-none');
        $submit.classList.add('d-none');
        $back.classList.add('d-none');

        const $progressContainer = $body.querySelector('#modal-progress-container') as HTMLElement;
        $progressContainer.classList.add('d-none');

        const $results = $body.querySelector('#modal-results') as HTMLElement;
        $results.classList.remove('d-none');

        const $title = m.Element.querySelector('.modal-title') as HTMLElement;
        $title.textContent = 'Your Results';

        const $typePageEl = document.querySelector('#page-types') as HTMLElement;
        const typePageData = JSON.parse($typePageEl?.dataset.pages || '') as typePage[];
        const typePages = typePageData.filter(page => page.product === product);

        const req: QuizRequest = {
            product,
            questions: Array.from($steps).map(getStepAnswer),
            typePages: typePages,
        };
        void axios.post<quizResponse>('/quizzes/type', req).then(resp => {
            const $spinner = $body.querySelector('#modal-results-spinner') as HTMLElement;
            $spinner.remove();

            const typePage = typePages.find(page => page.product === product && page.type === resp.data.type);

            const $content = $body.querySelector('#modal-results-content') as HTMLElement;
            $content.classList.remove('d-none');

            const $type = $body.querySelector('#modal-results-type') as HTMLElement;
            const $message = $body.querySelector('#modal-results-message') as HTMLElement;
            const converter = new showdown.Converter();
            $message.innerHTML = converter.makeHtml(resp.data.message);

            const $img = document.querySelector<HTMLElement>(`[data-product="${CSS.escape(product.toLowerCase())}"][data-type="${CSS.escape(resp.data.type.toLowerCase())}"]`);
            if ($img) {
                document.querySelector('#modal-results-image')?.appendChild($img);
                $img?.classList.remove('d-none');
            }

            $type.textContent = resp.data.type;

            const $shopButton = $body.querySelector('#modal-results-shop-button') as HTMLAnchorElement;
            $shopButton.textContent = `Shop ${resp.data.type} ${shortProductPlural(product)}`;

            const id = storeQuizRequest(req);
            $shopButton.href = `${typePage?.link || ''}?quiz=${id}` || '';

            const $restart = $body.querySelector('#modal-restart') as HTMLButtonElement;
            $restart.classList.remove('d-none');
            $restart.addEventListener('click', () => {
                m.Close();
                showQuiz($el);
            });
        }).catch(err => {
            HandleRequestError(err);
            throw err;
        });
    });

    const m = new Modal({
        title: shortProductSingular(product) + ' Type Quiz',
        body: $body,
        width: '872px',
        cancelButton: false,
        appendClassBySelector: {
            '.modal-body': 'px-2 px-lg-20 pb-14',
            '.modal-footer': 'd-none',
            '.modal-title': 'fs-2 fw-900',
            '.modal-header': 'pb-4',
        },
    });

    nextStep();
}

// check if element was clicked with [data-product-type-quiz-form] attribute
document.addEventListener('click', e => {
    const $el = e.target as HTMLElement;
    if ($el.hasAttribute('data-product-type-quiz-for')) {
        e.preventDefault();

        showQuiz($el);
    }
});